// src/components/SingleStoryDetail.js
import React, { useState, useEffect } from 'react';
import { useParams as useParamsRouter, Link } from 'react-router-dom';
import Button from './Button';
import LinkButton from './LinkButton';
import Background from './Background';
import Menu from './Menu';
import ButtonTh from './ButtonTh';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSlash, faCaretRight, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import md5 from 'md5';
import FooterList from './FooterList';
import Seo from './Seo';

const SingleStoryDetail = ({ onBack }) => {
  const { storyId } = useParamsRouter();
  //console.log('Story ID:', storyId);

  const [story, setStory] = useState(null);
  const [copyright, setCopyright] = useState('');

  const addResourceId = resource => {
    const regex = /\/(\d+)$/;
    const match = resource.resourceURI.match(regex);
    const resourceId = match ? match[1] : null;

    return {
      ...resource,
      resourceId
    };
  };
  function extractLastDigitsFromLink(link) {
    const regex = /(\d+)$/;
    const match = link.match(regex);
    return match ? match[1] : null;
  }
  useEffect(() => {
    const fetchStory = async () => {
      try {
        const publicKey = process.env.REACT_APP_MARVEL_PUBLIC_KEY;
        const privateKey = process.env.REACT_APP_MARVEL_PRIVATE_KEY;

        const timestamp = new Date().getTime();
        const hash = md5(`${timestamp}${privateKey}${publicKey}`);

        const response = await axios.get(
          `https://gateway.marvel.com/v1/public/stories/${storyId}?apikey=${publicKey}&ts=${timestamp}&hash=${hash}`
        );

        const storyData = response.data.data.results[0];

        // Dodaj identyfikatory do sekcji comics
        const storyWithComicsId = {
          ...storyData,
          comics: {
            items: storyData.comics.items.map(comic => addResourceId(comic))
          }
        };

        // Dodaj identyfikatory do sekcji series
        const storyWithSeriesId = {
          ...storyWithComicsId,
          series: {
            items: storyWithComicsId.series.items.map(series => addResourceId(series))
          }
        };

        // Dodaj identyfikatory do sekcji characters
        const storyWithCharactersId = {
          ...storyWithSeriesId,
          characters: {
            items: storyWithSeriesId.characters.items.map(character => addResourceId(character))
          }
        };

        // Dodaj identyfikatory do sekcji creators
        const storyWithCreatorsId = {
          ...storyWithCharactersId,
          creators: {
            items: storyWithCharactersId.creators.items.map(creator => addResourceId(creator))
          }
        };

        
        const copyrightText = response.data.attributionText;
        setCopyright(copyrightText);
        setStory(storyWithCreatorsId);
      } catch (error) {
        console.error('Error fetching story:', error);
      }
    };

    fetchStory();
  }, [storyId]);

  if (!story) {
    return (
      <Background>
      <div className='flex flex-col'>
        <p className='text-xl text-slate-200'>Loading...</p>
        <img className='w-48 items-center' src="/images/logo/loading-29.gif" alt="loading-29"/>
        <FooterList textVariable={copyright} />
      </div>
    </Background>
    );
  }

  return (
    <Background>
    <Menu textVariable='Story detail' />
      <div className='flex flex-col'>
      <Seo 
      title={story.title} 
      description={`Story info - ${story.originalIssue.name}`} 
      type='article'
      image_link={`${story.thumbnail?.path}/portrait_uncanny.${story.thumbnail?.extension}`}
      keywords='mv-app, mvapp'
      url=''
    />
        <div className='text-2xl text-slate-50 mb-2'>{story.title || 'Story Title Not Available'}</div>
        <p className='text-2xl text-slate-50 mb-2 shadow-md'>Description story:</p>
        <p className='text-xl text-slate-100 mb-2'>
          {story.description ? (
            story.description
          ) : (
            <span className='text-xl'>
              <FontAwesomeIcon icon={faCommentSlash} />&emsp;No description
            </span>
          )}
        </p>
        <p className='text-2xl text-slate-50 mb-2 shadow-md'>Original issue:</p>
        <p className='text-xl text-slate-100 mb-2'>
        {story.originalIssue ? (
            <span className='text-xl'><FontAwesomeIcon icon={faCaretRight} />&emsp;{story.originalIssue.name}</span>
          ) : (
            <span className='text-xl'>
              <FontAwesomeIcon icon={faCommentSlash} />&emsp;No original issue
            </span>
          )}
        </p>

        <div className='flex flex-col'>
          <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 mb-2 shadow-md'>Comics:</p>
            <ul>
              {story.comics.items.length > 0 ? (
                story.comics.items.map((comic) => (
                  <li className='text-l text-slate-50 ml-2 truncate' key={comic.resourceURI}>
                    <LinkButton to={`/comics/${comic.resourceId}`}>
                      <Button>Comic book</Button>
                    </LinkButton>
                    &emsp;<FontAwesomeIcon icon={faCaretRight} />&emsp;{comic.name}
                  </li>
                ))
              ) : (
                <li className='text-l text-slate-100 ml-2 '>
                  <FontAwesomeIcon icon={faCircleQuestion} />&emsp;No comics
                </li>
              )}
            </ul>
          </div>
          <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 mb-2 shadow-md'>Series:</p>
            <ul>
              {story.series.items.length > 0 ? (
                story.series.items.map((series) => (
                  <li className='text-l text-slate-100 ml-2 truncate' key={series.resourceURI}>
                    <LinkButton to={`/serie/${series.resourceId}`}>
                      <Button>Series info</Button>
                    </LinkButton>
                    &emsp;<FontAwesomeIcon icon={faCaretRight} />&emsp;{series.name}
                  </li>
                ))
              ) : (
                <li className='text-l text-slate-100 ml-2'>
                  <FontAwesomeIcon icon={faCircleQuestion} />&emsp;No series
                </li>
              )}
            </ul>
          </div>
          <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 mb-2 shadow-md'>Characters:</p>
            <ul>
              {story.characters.items.length > 0 ? (
                story.characters.items.map((character) => (
                  <li className='text-l text-slate-100 ml-2' key={character.resourceURI}>
                  <LinkButton to={`/characters/${character.resourceId}`}>
                    <Button>More info</Button>
                  </LinkButton>
                  &emsp;<FontAwesomeIcon icon={faCaretRight} />&emsp;{character.name}
                  </li>
                ))
              ) : (
                <li className='text-l text-slate-100 ml-2'>
                  <FontAwesomeIcon icon={faCircleQuestion} />&emsp;No characters
                </li>
              )}
            </ul>
          </div>
          <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 mb-2 shadow-md'>Creators:</p>
            <ul>
              {story.creators.items.length > 0 ? (
                story.creators.items.map((creator) => (
                  <li className='text-l text-slate-100 ml-2' key={creator.resourceURI}>
                  <LinkButton to={`/creator/${creator.resourceId}`}>
                    <Button>More info</Button>
                  </LinkButton>
                  &emsp;<FontAwesomeIcon icon={faCaretRight} />&emsp;{creator.name}&emsp;{creator.role}
                  </li>
                ))
              ) : (
                <li className='text-l text-slate-100 ml-2'>
                <FontAwesomeIcon icon={faCircleQuestion} />&emsp;No creators
              </li>
            )}
            </ul>
        </div>
        <div className='w-full mb-2'>
          <p className='text-2xl text-slate-50 mb-2 shadow-md'>Events:</p>
          <ul>
            {story.events.items.length > 0 ? (
              story.events.items.map((event) => {
                const eventId = extractLastDigitsFromLink(event.resourceURI);
                return (
                  <li className='text-l text-slate-100 ml-2' key={event.resourceURI}>
                    <LinkButton to={`/event/${eventId}`}>
                      <Button>More info</Button>
                    </LinkButton>           
                    &emsp;<FontAwesomeIcon icon={faCaretRight} />&emsp;{event.name}
                  </li>
                );
              })
            ) : (
              <li className='text-l text-slate-100 ml-2'>
                <FontAwesomeIcon icon={faCircleQuestion} />&emsp;No events
              </li>
            )}
          </ul>
        </div>


      </div>

      <div className='flex flex-row w-full justify-center items-center py-4 border-t border-slate-100'>
        <LinkButton to="/stories"><ButtonTh>List stories</ButtonTh></LinkButton>
      </div>

    
      <FooterList textVariable={copyright} />

    </div>
  </Background>
  );
};

export default SingleStoryDetail;
